import {
  lazy, Suspense, useEffect, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Button, Icon } from '@gupshup-frontend/gupshup-ts-components';

// emoji-picker-react is a large dependency — load it as its own chunk, fetched only once someone
// actually opens a picker, instead of shipping it in the main bundle every visitor downloads.
// Module scope, not inside the component below — a lazy() minted during render would remount
// (and re-suspend) on every render instead of once per module load.
const EmojiPicker = lazy(() => import('emoji-picker-react'));

// Same CDN convention sections/Body.js's own toolbar icons use (gs-ui-templates/src/components/
// WhatsappEditor/config.ts) — kept as its own one-line constant here rather than imported from
// Body.js, since Body.js needs the shared prefix for five OTHER toolbar icons this component has
// nothing to do with.
const EMOJI_ICON_URL = 'https://gs-upload.gupshup.io/templates/icons/ic_emoji.svg';

// Exact props handed to <EmojiPicker> below. Fixed constants, not measured off the DOM — the
// popover's position has to be computed the instant it opens, before the lazily-loaded library
// has fetched, let alone rendered something of its own to measure.
const POPOVER_WIDTH = 300;
const POPOVER_HEIGHT = 360;
const POPOVER_GAP = 6;
const VIEWPORT_MARGIN = 8;

// Pure, no React — the trigger's own rect plus the current viewport size in, a viewport-fixed
// {top, left} for the portaled popover out. Prefers opening below the trigger (the common case
// reads top-to-bottom); falls back to above when the trigger sits low enough that the popover
// wouldn't fit under it; and if neither side has room (a very short viewport), takes whichever
// side has more space and clamps into the viewport rather than letting the popover run off-screen.
const computePopoverPosition = (anchorRect, viewportWidth, viewportHeight) => {
  const spaceBelow = viewportHeight - anchorRect.bottom;
  const spaceAbove = anchorRect.top;

  let top;
  if (spaceBelow >= POPOVER_HEIGHT + POPOVER_GAP) {
    top = anchorRect.bottom + POPOVER_GAP;
  } else if (spaceAbove >= POPOVER_HEIGHT + POPOVER_GAP) {
    top = anchorRect.top - POPOVER_HEIGHT - POPOVER_GAP;
  } else {
    top = spaceBelow >= spaceAbove
      ? anchorRect.bottom + POPOVER_GAP
      : anchorRect.top - POPOVER_HEIGHT - POPOVER_GAP;
    top = Math.min(
      Math.max(top, VIEWPORT_MARGIN),
      viewportHeight - POPOVER_HEIGHT - VIEWPORT_MARGIN,
    );
  }

  const left = Math.min(
    Math.max(anchorRect.left, VIEWPORT_MARGIN),
    viewportWidth - POPOVER_WIDTH - VIEWPORT_MARGIN,
  );

  return { top, left };
};

// Shared emoji-picker trigger + popover, extracted out of Body.js so Carousel.js's card
// descriptions can use the identical picker. Owns its own open/close state and outside-click/
// Escape handling; the caller owns everything about WHERE the emoji goes (caret position, max-
// length guard, focus restore) via onEmojiSelect — this component only ever hands back the plain
// emoji string, never the picker library's own event shape.
const EmojiPickerButton = ({
  onEmojiSelect, disabled, dataTestId, popoverTestId,
}) => {
  const anchorRef = useRef(null);
  const popoverRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    // The popover portals onto document.body (render below), so it's no longer a DOM descendant
    // of anchorRef's wrapper — a click on one of its emoji buttons has to count as "inside" too,
    // or it reads as an outside click and closes the picker before onEmojiClick ever fires.
    const handleOutsideClick = (event) => {
      const insideAnchor = anchorRef.current && anchorRef.current.contains(event.target);
      const insidePopover = popoverRef.current && popoverRef.current.contains(event.target);
      if (!insideAnchor && !insidePopover) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // QA: on a trigger button that sits low on the page (e.g. a carousel card's description, near
  // the bottom of a long form), the popover used to open downward unconditionally per its own CSS
  // and get clipped — with the page already at max scroll there was nothing left to scroll it
  // into view, and an ancestor's own overflow clipped it regardless of scroll position. Fixed by
  // portaling the popover to document.body (render below, `position: fixed` in templates.css) and
  // computing its on-screen position here instead of leaving it to normal-flow CSS: measure the
  // trigger the instant the picker opens, flip above/below/clamped per computePopoverPosition,
  // and keep it pinned to the trigger across window resizes and scrolls of ANY ancestor — scroll
  // doesn't bubble, but a capture-phase listener still sees it, hence capture: true. Cleared back
  // to null on close so a stale position from the last open can't flash before this effect
  // recomputes on the next one.
  useEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return undefined;
    }

    const reposition = () => {
      if (!anchorRef.current) {
        return;
      }
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition(computePopoverPosition(rect, window.innerWidth, window.innerHeight));
    };

    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [isOpen]);

  const handleEmojiClick = (emojiData) => {
    onEmojiSelect(emojiData.emoji);
    setIsOpen(false);
  };

  return (
    <div className="br-tpl-form-emoji-picker-anchor" ref={anchorRef}>
      <Button
        size="small"
        variant="text"
        disabled={disabled}
        onClick={() => setIsOpen((open) => !open)}
        data-testid={dataTestId}
      >
        <Icon customPath={EMOJI_ICON_URL} size={18} alt="Emoji" />
      </Button>
      {isOpen && !disabled && position && createPortal(
        <div
          className="br-tpl-form-emoji-picker-popover"
          data-testid={popoverTestId || `${dataTestId}-popover`}
          ref={popoverRef}
          style={{ top: position.top, left: position.left }}
        >
          <Suspense fallback={null}>
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              autoFocusSearch={false}
              lazyLoadEmojis
              width={POPOVER_WIDTH}
              height={POPOVER_HEIGHT}
            />
          </Suspense>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default EmojiPickerButton;
